import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Calendar as CalendarIcon } from "lucide-react";

function fmtShort(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}
function daysUntil(iso) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso + "T00:00:00");
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff < 7) return `In ${diff} days`;
  if (diff < 14) return "Next week";
  return "";
}

export function UpcomingHolidaysCard({ site, limit = 4, testid = "upcoming-holidays" }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    const params = { limit };
    if (site) params.site = site;
    api.get("/public-holidays/upcoming", { params })
      .then(({ data }) => setItems(data.holidays || []))
      .catch(() => setItems([]));
  }, [site, limit]);

  if (!items) return null;

  // De-dup by date (NATIONAL + SITE_x with same date/name become one row)
  const seen = new Set();
  const holidays = items.filter((h) => {
    const key = h.date + "|" + h.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return (
    <Card className="border-gray-200" data-testid={testid}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold text-[#1A2744] flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} />
            Upcoming public holidays
          </h3>
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">NZ</span>
        </div>
        {holidays.length === 0 ? (
          <p className="text-sm text-gray-500">No upcoming holidays in the next stretch.</p>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid={`${testid}-list`}>
            {holidays.map((h) => {
              const regional = h.scope !== "NATIONAL";
              return (
                <li
                  key={`${h.scope}-${h.date}-${h.name}`}
                  className="py-2.5 flex items-center justify-between gap-3"
                  data-testid={`${testid}-item`}
                  data-holiday-item="true"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A2744] truncate">{h.name}</div>
                    <div className="text-[11px] text-gray-500">
                      {fmtShort(h.date)}
                      {regional && <span className="ml-1.5 text-[#C9A84C]">· Regional</span>}
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">{daysUntil(h.date)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
